// Acesso a dados de `projects` para a UI (Etapa 10 — telas de Projetos).
// Toda escrita passa pelos helpers transacionais de src/db/writes.ts
// (SQLite + outbox na mesma transação) — regra invíolável #2 do projeto.
// Leituras são `select` direto (Drizzle), sem passar por writes.ts (que só
// existe para mutação).
import { and, eq, isNull } from 'drizzle-orm';
import type { InferInsertModel } from 'drizzle-orm';
import {
  projects,
  projectSupplies,
  supplies,
  counters,
  timeEntries,
  projectPhotos,
  type Project,
  type NewProject,
} from '../../db/schema';
import { insertRecord, updateRecord, softDeleteRecord } from '../../db/writes';
import type { AppDatabase } from '../../db/outbox';

/** Lista todos os projetos não excluídos (soft delete) da usuária. Sem
 * filtro/ordenação — isso é responsabilidade pura de
 * `src/domain/projectList.ts#filterAndSortProjects`, aplicada pela tela
 * sobre o array retornado aqui. */
export async function listProjects(db: AppDatabase, userId: string): Promise<Project[]> {
  return db
    .select()
    .from(projects)
    .where(and(eq(projects.userId, userId), isNull(projects.deletedAt)));
}

export async function getProject(db: AppDatabase, id: string): Promise<Project | undefined> {
  return db
    .select()
    .from(projects)
    .where(and(eq(projects.id, id), isNull(projects.deletedAt)))
    .get();
}

/** Criar projeto: só o nome é obrigatório (regra da spec S02) — todo o
 * resto fica com o default do schema (`status = 'not_started'` etc.) e é
 * preenchível depois na tela de detalhe. */
export async function createProject(db: AppDatabase, userId: string, name: string): Promise<Project> {
  return insertRecord(db, projects, { userId, name });
}

export type ProjectPatch = Partial<Pick<NewProject, 'name' | 'status' | 'notes' | 'recipeUrl'>>;

export async function updateProject(db: AppDatabase, id: string, patch: ProjectPatch): Promise<Project> {
  return updateRecord(db, projects, id, patch);
}

/** Forma explícita (não derivada genericamente) do patch de overrides de
 * preço — mesma razão de `CounterNamePatch` (src/features/session/counters.ts):
 * `InferInsertModel<TTable>` combinado com o generic de união
 * `WritableTable` (src/db/writes.ts) resolve mal colunas opcionais/nullable
 * como estas. O cast no ÚNICO ponto de chamada a `updateRecord`
 * (`updateProjectPricingOverrides`, abaixo) contorna a limitação sem perder
 * a checagem de tipo na montagem do patch em si. */
interface ProjectPricingOverridesPatch {
  hourlyRateOverride: number | null;
  markupPctOverride: number | null;
  extraCosts: number;
  saleFeePct: number;
}

/** Salva os overrides de preço por projeto (Etapa 13 — spec S04): valor da
 * hora e mark-up (`null` = usa o valor do perfil, só NESTE projeto) e os
 * campos avançados `extraCosts`/`saleFeePct` (sempre um número, nunca
 * `null` — já são `NOT NULL DEFAULT 0` no schema). Só grava, nunca calcula
 * nada — quem calcula é sempre `domain/pricing.ts`. */
export async function updateProjectPricingOverrides(
  db: AppDatabase,
  id: string,
  patch: ProjectPricingOverridesPatch
): Promise<Project> {
  return updateRecord(db, projects, id, patch as Partial<InferInsertModel<typeof projects>>);
}

/** Forma explícita do patch de preço praticado — ver comentário acima em
 * `ProjectPricingOverridesPatch`. */
interface ProjectPracticedPricePatch {
  practicedPrice: number | null;
}

/** Grava o preço praticado (Etapa 13 — spec S04): campo INFORMATIVO
 * preenchido ao finalizar o projeto, `null` quando a usuária pula o
 * diálogo. NUNCA entra em nenhum cálculo de `domain/pricing.ts` — regra
 * invólavel #4 do projeto, exceção deliberada só para este campo (ver
 * CLAUDE.md e o comentário em `src/db/schema.ts#projects.practicedPrice`). */
export async function setProjectPracticedPrice(
  db: AppDatabase,
  id: string,
  practicedPrice: number | null
): Promise<Project> {
  const patch: ProjectPracticedPricePatch = { practicedPrice };
  return updateRecord(db, projects, id, patch as Partial<InferInsertModel<typeof projects>>);
}

/**
 * Soft delete do projeto + soft delete em cascata (lógica) de tudo que
 * pertence a ele — regra da spec S02 ("excluir projeto exclui em cascata os
 * insumos vinculados, fotos, contadores e sessões de tempo"): vínculos de
 * insumo (`project_supplies`), contadores (`counters`), sessões de tempo
 * (`time_entries`) e fotos (`project_photos`). Não mexe na tabela `supplies`
 * em si: o insumo continua no catálogo pessoal, só o vínculo com ESTE
 * projeto some (garante que ele não "vaza" para nenhum relatório futuro que
 * some insumos por projeto).
 *
 * `counter_events` (filhos append-only dos contadores) fica FORA da
 * cascata: são registros imutáveis sem coluna `deleted_at`/semântica de
 * soft delete própria — ver src/db/writes.ts#insertCounterEvent. A fronteira
 * da cascata é `counters`, não seus eventos.
 *
 * Cada tabela usa sua própria query+loop (em vez de um helper genérico sobre
 * as 4 tabelas) pelo mesmo motivo documentado em src/db/writes.ts: Drizzle
 * não infere bem sobre um generic unindo tabelas heterogêneas.
 */
export async function deleteProject(db: AppDatabase, id: string): Promise<void> {
  const links = await db
    .select()
    .from(projectSupplies)
    .where(and(eq(projectSupplies.projectId, id), isNull(projectSupplies.deletedAt)));
  for (const link of links) {
    await softDeleteRecord(db, projectSupplies, link.id);
  }

  const projectCounters = await db
    .select()
    .from(counters)
    .where(and(eq(counters.projectId, id), isNull(counters.deletedAt)));
  for (const counter of projectCounters) {
    await softDeleteRecord(db, counters, counter.id);
  }

  const entries = await db
    .select()
    .from(timeEntries)
    .where(and(eq(timeEntries.projectId, id), isNull(timeEntries.deletedAt)));
  for (const entry of entries) {
    await softDeleteRecord(db, timeEntries, entry.id);
  }

  const photos = await db
    .select()
    .from(projectPhotos)
    .where(and(eq(projectPhotos.projectId, id), isNull(projectPhotos.deletedAt)));
  for (const photo of photos) {
    await softDeleteRecord(db, projectPhotos, photo.id);
  }

  await softDeleteRecord(db, projects, id);
}

export interface ProjectSupplyView {
  id: string;
  supplyId: string;
  quantity: number;
  /** Preço da época do vínculo — NUNCA o preço atual do catálogo (ver
   * linkSupply.ts). É o que a tela de detalhe deve exibir/somar. */
  unitPriceSnapshot: number;
  supplyName: string;
  supplyUnit: string;
  supplyType: string;
}

/**
 * Insumos vinculados a um projeto, já combinados com os dados de exibição
 * do catálogo (nome/unidade/tipo). Duas consultas simples (sem JOIN) em vez
 * de uma junção Drizzle: este projeto não tem nenhum uso comprovado de
 * `.innerJoin` sobre o driver assíncrono `drizzle-orm/sqlite-proxy` (ver
 * comentário grande em src/db/client.ts sobre pegadinhas específicas desse
 * driver) — para o volume de insumos por projeto (poucas dezenas no
 * máximo), duas queries é simples, testável e comprovadamente seguro nesse
 * driver.
 *
 * Note que o nome/unidade/tipo vêm do estado ATUAL do insumo no catálogo
 * (não há snapshot desses campos, só do preço) — se o insumo foi
 * renomeado ou removido do catálogo depois, esta view reflete o estado
 * atual dele (ou fica em branco se o insumo já não existir mais).
 */
export async function listProjectSupplies(db: AppDatabase, projectId: string): Promise<ProjectSupplyView[]> {
  const links = await db
    .select()
    .from(projectSupplies)
    .where(and(eq(projectSupplies.projectId, projectId), isNull(projectSupplies.deletedAt)));

  const views: ProjectSupplyView[] = [];
  for (const link of links) {
    const supply = await db.select().from(supplies).where(eq(supplies.id, link.supplyId)).get();
    views.push({
      id: link.id,
      supplyId: link.supplyId,
      quantity: link.quantity,
      unitPriceSnapshot: link.unitPriceSnapshot,
      supplyName: supply?.name ?? '',
      supplyUnit: supply?.unit ?? '',
      supplyType: supply?.type ?? '',
    });
  }
  return views;
}
